import { useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useOn } from "@/lib/features";
import { Chips, Loading, PageHead, Stat, money, useAdmin } from "./ui";
import { StockTable } from "./StockTable";
import { AppInstalls } from "./AppInstalls";

const secs = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

const ListCard = ({ title, rows, testId }) => (
  <div className="ad-card" data-testid={testId}>
    <h3>{title}</h3>
    {rows.length === 0 && <p className="muted-sm">No data yet.</p>}
    {rows.map(([k, v]) => <div className="ad-row" key={k}><span>{k}</span><strong>{v}</strong></div>)}
  </div>
);

export default function Dashboard() {
  const [days, setDays] = useState("30");
  const { data } = useAdmin(`analytics-${days}`, `/admin/analytics?days=${days}`);
  const shopOn = useOn("shop");
  return (
    <div data-testid="admin-dashboard">
      <PageHead eyebrow="Overview" title="Dashboard">
        <Chips options={[["7", "7 days"], ["30", "30 days"], ["90", "90 days"]]} value={days} onChange={setDays} testPrefix="analytics-range" />
      </PageHead>
      {!data ? <Loading /> : (
        <>
          <div className="ad-stats">
            <Stat label="Visitors" value={data.visitors} testId="stat-visitors" />
            <Stat label="Page views" value={data.pageviews} testId="stat-pageviews" />
            <Stat label="Avg. visit" value={secs(data.avg_session_seconds)} testId="stat-avg-visit" />
            <Stat label="Bookings" value={data.bookings} hint={`${data.conversion}% of visits`} testId="stat-bookings" />
            <Stat label="Deposits paid" value={money(data.revenue)} testId="stat-revenue" />
            <Stat label="New subscribers" value={data.subscribers} testId="stat-subscribers" />
          </div>
          <div className="ad-card ad-chart" data-testid="analytics-chart">
            <h3>Visitors &amp; page views</h3>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.daily} margin={{ left: -20, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#c99a2e" stopOpacity={0.45} /><stop offset="1" stopColor="#c99a2e" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gp" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#e846a4" stopOpacity={0.3} /><stop offset="1" stopColor="#e846a4" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="#f0e6e2" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} tickLine={false} axisLine={false} minTickGap={20} />
                <YAxis allowDecimals={false} fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip />
                <Area type="monotone" dataKey="pageviews" name="Page views" stroke="#e846a4" fill="url(#gp)" strokeWidth={2} />
                <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#a87b17" fill="url(#gv)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="ad-grid3">
            <ListCard title="Top pages" rows={data.top_pages} testId="analytics-top-pages" />
            <ListCard title="Where visitors come from" rows={data.referrers} testId="analytics-referrers" />
            <ListCard title="Devices" rows={data.devices} testId="analytics-devices" />
            <ListCard title="Bookings by status" rows={data.bookings_by_status.map(([k, v]) => [k.replace("_", " "), v])} testId="analytics-bookings-status" />
          </div>
          <AppInstalls />
          {shopOn && <StockTable />}
        </>
      )}
    </div>
  );
}
